import { useOverlayState } from "@heroui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateExchangeCodeMutation } from "@/src/auth/services/auth-http.service";
import { Modal } from "@/src/common/components/ui";
import { buildCommunityUrl } from "@/src/common/lib/subdomain";
import { getRefreshToken } from "@/src/common/services/http.service";

interface CongratulationsModalProps {
	isOpen: boolean;
	communityName: string;
	communitySlug: string;
}

export function CongratulationsModal({ isOpen, communityName, communitySlug }: CongratulationsModalProps) {
	const { t } = useTranslation("onboarding");
	const [seconds, setSeconds] = useState(5);
	const [createExchangeCode] = useCreateExchangeCodeMutation();
	const redirecting = useRef(false);

	const state = useOverlayState({ isOpen, onOpenChange: () => {} });

	useEffect(() => {
		if (!isOpen) return;

		const interval = setInterval(() => {
			setSeconds((prev) => {
				if (prev <= 1) {
					clearInterval(interval);
					if (redirecting.current) return 0;
					redirecting.current = true;

					const refreshToken = getRefreshToken();
					const baseUrl = buildCommunityUrl(communitySlug);

					if (refreshToken) {
						createExchangeCode({ refresh_token: refreshToken })
							.unwrap()
							.then(({ code }) => {
								window.location.href = `${baseUrl}?auth_code=${encodeURIComponent(code)}`;
							})
							.catch(() => {
								window.location.href = baseUrl;
							});
					} else {
						window.location.href = baseUrl;
					}
					return 0;
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(interval);
	}, [isOpen, communitySlug, createExchangeCode]);

	return (
		<Modal state={state}>
			<Modal.Backdrop isDismissable={false}>
				<Modal.Container>
					<Modal.Dialog>
						<Modal.Body className="text-center py-8">
							<p className="text-4xl mb-4">🎉</p>
							<h2 className="text-xl font-bold mb-2">{t("congratulations_title")}</h2>
							<p className="text-lg font-medium mb-1">{t("congratulations_message", { communityName })}</p>
							<p className="text-sm text-foreground/50 mb-6">{t("congratulations_subtitle")}</p>
							<p className="text-sm text-foreground/40">{t("redirecting_in", { seconds })}</p>
						</Modal.Body>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}
